import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Loader2,
  Lock,
  Key,
  Shield,
  CheckCircle2,
  ArrowLeft,
  Eye,
  EyeOff,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { authAPI } from "@/services/api";
import { AnimatedBackground } from "@/components/ui/AnimatedBackground";

export function ResetPasswordForm() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const email = searchParams.get("email");
  const navigate = useNavigate();
  const { toast } = useToast();

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isTokenValid, setIsTokenValid] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    // Verificación simple: si hay token, mostramos el formulario.
    // La validación real ocurre en el backend al enviar.
    if (token) {
      setIsTokenValid(true);
    } else {
      setIsTokenValid(false);
    }
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!token) {
      toast({
        title: "Error",
        description: "Token no válido",
        variant: "destructive",
      });
      return;
    }

    if (newPassword !== confirmPassword) {
      toast({
        title: "Error",
        description: "Las contraseñas no coinciden",
        variant: "destructive",
      });
      return;
    }

    if (newPassword.length < 6) {
      toast({
        title: "Error",
        description: "La contraseña debe tener al menos 6 caracteres",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      await authAPI.resetPassword({
        token,
        new_password: newPassword,
        email: email || undefined,
      });

      setSuccess(true);
      toast({
        title: "¡Éxito!",
        description: "Tu contraseña ha sido actualizada correctamente",
        variant: "default",
      });

      setTimeout(() => {
        navigate("/login");
      }, 3000);
    } catch (err: any) {
      setError(err.message || "Ocurrió un error al restablecer la contraseña");
      toast({
        title: "Error",
        description: err.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate("/login");
  };

  if (isTokenValid === null) {
    return (
      <div className="min-h-screen overflow-hidden relative">
        <AnimatedBackground />
        <div className="relative z-10 flex items-center justify-center min-h-screen p-4">
          <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-slate-600">Validando enlace de recuperación...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!isTokenValid) {
    return (
      <div className="min-h-screen overflow-hidden relative">
        <AnimatedBackground />
        <div className="relative z-10 flex items-center justify-center min-h-screen p-4">
          <Card className="w-full max-w-md shadow-2xl">
          <CardContent className="p-6 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Key className="h-8 w-8 text-red-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-800 mb-2">
              Enlace Inválido
            </h2>
            <p className="text-slate-600 mb-6">
              Este enlace de recuperación ha expirado o no es válido.
            </p>
            <Button
              onClick={handleBackToLogin}
              className="w-full bg-blue-600 hover:bg-blue-700"
            >
              Volver al Login
            </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
      <div className="min-h-screen overflow-hidden relative">
        <AnimatedBackground />
        <div className="relative z-10 flex items-center justify-center min-h-screen p-4">
      <motion.div
        className="w-full max-w-md shadow-2xl rounded-2xl bg-white/95 backdrop-blur-sm overflow-hidden border border-white/20"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1, duration: 0.5 }}
      >
        <div className="w-full p-6">
          <motion.div
            className="flex items-center justify-start mb-2"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Button
              variant="ghost"
              onClick={handleBackToLogin}
              className="p-2 h-auto hover:bg-slate-100 text-slate-600 hover:text-slate-800 rounded-lg"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Volver
            </Button>
          </motion.div>

          <motion.div
            className="space-y-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <div className="text-center space-y-2">
              <div className="flex justify-center">
                <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                  <Key className="h-5 w-5 text-white" />
                </div>
              </div>
              <h1 className="text-2xl font-bold text-slate-800">
                Nueva Contraseña
              </h1>
              <p className="text-slate-600 text-sm">
                Crea una nueva contraseña para tu cuenta
              </p>
            </div>

            {success ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center space-y-4 p-6 bg-green-50 rounded-xl border border-green-200"
              >
                <div className="flex justify-center">
                  <CheckCircle2 className="h-12 w-12 text-green-500" />
                </div>

                <div className="space-y-2">
                  <h3 className="text-lg font-semibold text-slate-800">
                    ¡Contraseña Actualizada!
                  </h3>
                  <p className="text-sm text-slate-600">
                    Tu contraseña ha sido cambiada exitosamente
                  </p>
                  <p className="text-xs text-slate-500">
                    Redirigiendo al login...
                  </p>
                </div>
              </motion.div>
            ) : (
              <div className="space-y-4">
                  {error && (
                    <Alert
                      variant="destructive"
                      className="animate-in fade-in duration-300"
                    >
                      <AlertDescription className="text-sm">
                        {error}
                      </AlertDescription>
                    </Alert>
                  )}

                  <form onSubmit={handleSubmit} className="space-y-3">
                    <div className="space-y-1">
                      <Label
                        htmlFor="newPassword"
                        className="text-slate-700 text-sm font-medium"
                      >
                        Nueva Contraseña
                      </Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                          id="newPassword"
                          type={showPassword ? "text" : "password"}
                          placeholder="Mínimo 6 caracteres"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          className="pl-10 pr-10 h-11 border-slate-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                          required
                          disabled={isLoading}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        >
                          {showPassword ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </div>

                    <div className="space-y-1">
                      <Label
                        htmlFor="confirmPassword"
                        className="text-slate-700 text-sm font-medium"
                      >
                        Confirmar Contraseña
                      </Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                          id="confirmPassword"
                          type={showConfirmPassword ? "text" : "password"}
                          placeholder="Repite tu contraseña"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          className="pl-10 pr-10 h-11 border-slate-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                          required
                          disabled={isLoading}
                        />
                        <button
                          type="button"
                          onClick={() =>
                            setShowConfirmPassword(!showConfirmPassword)
                          }
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        >
                          {showConfirmPassword ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </div>

                    <div className="bg-blue-50 p-2.5 rounded-lg border border-blue-200">
                      <div className="flex items-start space-x-2">
                        <Shield className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-xs font-medium text-blue-800">
                            Requisitos de Seguridad
                          </p>
                          <p className="text-xs text-blue-700">
                            Mínimo 6 caracteres
                          </p>
                        </div>
                      </div>
                    </div>

                    <Button
                      type="submit"
                      className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white font-semibold shadow-md transition-all duration-200 disabled:opacity-50"
                      disabled={isLoading || !newPassword || !confirmPassword}
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                          Actualizando...
                        </>
                      ) : (
                        <>
                          <Key className="h-4 w-4 mr-2" />
                          Cambiar Contraseña
                        </>
                      )}
                    </Button>
                  </form>
              </div>
            )}
          </motion.div>
        </div>
        <div className="px-6 pb-4">
          <p className="text-xs text-slate-500 flex items-center justify-center">
            <Shield className="h-3 w-3 mr-1" />
            Proceso seguro y encriptado
          </p>
        </div>
      </motion.div>
    </div>
    </div>
  );
}
